import React, { useCallback, useMemo, useState } from 'react';
import { momentInstance } from 'helpers/date-time';
import DATE_FORMATS from 'consts/dateFormats';

//* README:
//* In case response return the time that has only date like: YYYY-MM-DD, default will be YYYY-MM-DD 00:00
const TimezoneContext = React.createContext({
  timezone: '',
  setNewTimezone: (nextTimezone) => {},
  showDateTimeByTimezone: (
    date,
    {
      format = 'lll',
      formatSpecification = undefined,
      fromUTCtoTimezone = true,
      keepRawDate = false,
      timezone = '',
      returnMomentObject = false,
    } = {},
  ) => {},
});

export const useTimeHelpers = () => React.useContext(TimezoneContext);

const KEY_TIMEZONE = 'app-timezone';

//* For use in external components
export const timezoneHelpers = {
  loadTimezoneFromStorage: () => {
    return localStorage.getItem(KEY_TIMEZONE) || 'Etc/GMT';
  },

  /**
   * @param {Moment | DateObj} date
   * @param {Object} options
   * @param {"start-of-date" | "end-of-date"} options.type
   * @param {"timestamp" | "moment-obj"} options.returnType
   * @param {boolean} options.keepLocalTime - If enabled, it will not be converted to UTC
   * @returns {number | MomentObj} - if returnType === "timestamp" -> the result is number | Default: MomentObj
   */
  convertDateTimeToUTC: function (date, { type, keepLocalTime, returnType = 'moment-obj' } = {}) {
    if (!date) {
      return null;
    }

    let momentObj = momentInstance(date);

    if (type === 'start-of-date') {
      momentObj = momentInstance(date).startOf('date');
    }

    if (type === 'end-of-date') {
      momentObj = momentInstance(date).endOf('date');
    }

    //* Temporary: If the datetime contains only the date, it will not be converted to UTC
    if (keepLocalTime) {
      return momentObj;
    }

    return returnType === 'timestamp'
      ? momentObj.tz(this.loadTimezoneFromStorage(), true).utc().unix() * 1000
      : momentObj.tz(this.loadTimezoneFromStorage(), true).utc();
  },

  /**
   *
   * @param {MomentObj | Date} date
   * @param {Object} options
   * @param {string} options.format
   * @param {string} options.formatSpecification
   * @param {boolean} options.fromUTCtoTimezone
   * @param {boolean} options.keepRawDate
   * @param {string} options.timezone - timezone IANA format
   * @param {boolean} options.returnMomentObject
   * @param {boolean} options.isOnlyDate - if response return only date without time -> should be convert time to 00:00
   * @returns
   */
  showDateTimeByTimezone: function (
    date,
    {
      format = 'lll',
      formatSpecification = undefined,
      fromUTCtoTimezone = true,
      keepRawDate = false,
      timezone = this.loadTimezoneFromStorage(),
      returnMomentObject = false,
      isOnlyDate = false,
    } = {},
  ) {
    if (!date) {
      return '';
    }

    let momentObj = momentInstance;

    if (fromUTCtoTimezone) {
      momentObj = momentInstance.utc;
    }

    if (returnMomentObject) {
      return momentObj(date, formatSpecification).tz(timezone);
    }

    if (keepRawDate) {
      return momentObj(date, formatSpecification).format(format);
    }

    return isOnlyDate
      ? momentObj(date, formatSpecification).tz(timezone).startOf('date').format('ll')
      : momentObj(date, formatSpecification).tz(timezone).format(format);
  },

  /**
   * Convert a date range (`from`, `to`) into UTC-based values with flexible return options.
   *
   * @function convertDateRangeByTimezone
   * @param {Object} params - Parameters for conversion.
   * @param {Date|string|number} params.from - Start date of the range (can be a Date object, timestamp, or date string).
   * @param {Date|string|number} params.to - End date of the range (can be a Date object, timestamp, or date string).
   * @param {string} params.format - Date format string (e.g., `'YYYY-MM-DD HH:mm:ss'`), used when returning formatted strings.
   * @param {boolean} [params.dateOnly=false] - If `true`, only the date part is considered (time is ignored).
   * @param {boolean} [params.shouldReturnTimestamp=false] - If `true`, returns timestamps instead of formatted strings.
   * @returns {{ from: string|number|undefined, to: string|number|undefined }}
   * - An object containing converted `from` and `to` values:
   *   - If `shouldReturnTimestamp` is true → numeric timestamps.
   *   - If `dateOnly` is true → formatted date-only strings.
   *   - Otherwise → formatted date-time strings.
   *
   * @throws {Error} If both `from` and `to` are missing, a console error is logged and `{ from: undefined, to: undefined }` is returned.
   *
   * @example
   * // Return formatted date range
   * convertDateRangeByTimezone({
   *   from: new Date(),
   *   to: new Date(),
   *   format: 'YYYY-MM-DD HH:mm:ss'
   * });
   *
   * @example
   * // Return only date portion
   * convertDateRangeByTimezone({
   *   from: '2025-09-23',
   *   to: '2025-09-24',
   *   format: 'YYYY-MM-DD',
   *   dateOnly: true
   * });
   *
   * @example
   * // Return timestamps
   * convertDateRangeByTimezone({
   *   from: new Date(),
   *   to: new Date(),
   *   shouldReturnTimestamp: true
   * });
   */
  convertDateRangeByTimezone: function ({ from, to, format, shouldReturnTimestamp }) {
    if (!from || !to) {
      console.error('From / To Date need to be defined!');
      return {
        from: undefined,
        to: undefined,
      };
    }

    if (shouldReturnTimestamp) {
      return {
        from: this.convertDateTimeToUTC(from, { returnType: 'timestamp' }),
        to: this.convertDateTimeToUTC(to, { returnType: 'timestamp' }),
      };
    }

    const dateOnly = [DATE_FORMATS.DATE_ONLY, 'DD/MM/YYYY'].includes(format);
    if (dateOnly) {
      const tempFrom = this.convertDateTimeToUTC(from, { keepLocalTime: true })
        .startOf('date')
        .format(format);
      const tempTo = this.convertDateTimeToUTC(to, { keepLocalTime: true })
        .startOf('date')
        .format(format);

      return {
        from: tempFrom,
        to: tempTo,
      };
    }

    return {
      from: this.convertDateTimeToUTC(from).format(format),
      to: this.convertDateTimeToUTC(to).format(format),
    };
  },

  convertUTCToDateTimeByTimeZone: function ({ from, to }) {
    return {
      from: momentInstance(from).tz(this.loadTimezoneFromStorage()),
      to: momentInstance(to).tz(this.loadTimezoneFromStorage()),
    };
  },
};

const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezone] = useState(() => timezoneHelpers.loadTimezoneFromStorage());

  const showDateTimeByTimezone = useCallback(
    (date, options) => {
      return timezoneHelpers.showDateTimeByTimezone(date, { ...options, timezone });
    },
    [timezone],
  );

  const setNewTimezone = useCallback(
    (newTimeZone) => {
      setTimezone(newTimeZone);
      localStorage.setItem(KEY_TIMEZONE, newTimeZone);
    },
    [setTimezone],
  );

  const value = useMemo(() => {
    return {
      timezone,
      setNewTimezone,
      showDateTimeByTimezone,
    };
  }, [setNewTimezone, timezone, showDateTimeByTimezone]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

export default TimezoneProvider;
